/**
 * Trend chart builders for the Marketing tab (quick-261104-ia3, John
 * 2026-11-04: "every Marketing tile must be trended over time").
 *
 * Pure functions — no React — so they unit-test without a canvas:
 *  - monthlyStackOption: monthly stacked columns (top 5 + "Other").
 *  - quarterLinesOption: one line per series by quarter, gaps where empty.
 * Categorical colours follow the fixed SERIES order (blue, orange, aqua, red,
 * violet); "Other" is always a neutral grey so it never reads as a category.
 */
import { BAR_RADIUS, INK, SERIES, baseOption, tipHead, tipNote, tipRow } from './chart.js';
import { money, monthLabel, num, pct } from './fmt.js';

import type { ExecMonthlyStack, ExecQuarterLines } from '@saledash/shared';
import type { EChartsOption } from 'echarts';

export const CATEGORY_ORDER: string[] = [SERIES.blue, SERIES.orange, SERIES.aqua, SERIES.red, SERIES.violet];
export const OTHER_COLOR = '#b4bfcc';

/** Colour per series name, in order: categories take the fixed palette, 'Other' is grey. */
export function seriesColors(names: string[]): string[] {
  let i = 0;
  return names.map((n) => {
    if (n.trim().toLowerCase() === 'other') return OTHER_COLOR;
    const c = CATEGORY_ORDER[i % CATEGORY_ORDER.length] ?? INK.muted;
    i += 1;
    return c;
  });
}

/** Legend items for an HTML <Legend>. */
export function legendItems(series: { name: string }[]): { label: string; color: string }[] {
  const colors = seriesColors(series.map((s) => s.name));
  return series.map((s, i) => ({ label: s.name, color: colors[i] ?? OTHER_COLOR }));
}

export type StackUnit = 'count' | 'money';
export type LineUnit = 'percent' | 'days';

export function fmtStack(unit: StackUnit, v: number): string {
  return unit === 'money' ? money(v) : num(v);
}
export function fmtLine(unit: LineUnit, v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return unit === 'percent' ? pct(v) : `${String(Math.round(v))} days`;
}

/** Per-month totals across every series. */
export function monthTotals(stack: ExecMonthlyStack): number[] {
  return stack.months.map((_, i) => stack.series.reduce((s, x) => s + (x.values[i] ?? 0), 0));
}

/** Headline numbers for a monthly stack: 12-month total and the latest month. */
export function stackSummary(stack: ExecMonthlyStack | undefined): { total: number; lastMonth: string | null; lastValue: number } | null {
  if (!stack || stack.months.length === 0) return null;
  const totals = monthTotals(stack);
  const last = stack.months.length - 1;
  return { total: totals.reduce((s, v) => s + v, 0), lastMonth: stack.months[last] ?? null, lastValue: totals[last] ?? 0 };
}

/** True when there is nothing to draw. */
export function isEmptyStack(stack: ExecMonthlyStack | undefined): boolean {
  return !stack || stack.months.length === 0 || stack.series.every((s) => s.values.every((v) => !v));
}
export function isEmptyLines(lines: ExecQuarterLines | undefined): boolean {
  return !lines || lines.quarters.length === 0 || lines.series.every((s) => s.values.every((v) => v == null));
}

/** Monthly stacked columns, oldest → newest, tooltip lists each series and the month total. */
export function monthlyStackOption(stack: ExecMonthlyStack, opts: { unit: StackUnit; note?: string }): EChartsOption {
  const base = baseOption({ yMoney: opts.unit === 'money', left: opts.unit === 'money' ? 48 : 40 });
  const colors = seriesColors(stack.series.map((s) => s.name));
  const totals = monthTotals(stack);
  const lastIdx = stack.series.length - 1;
  return {
    ...base,
    xAxis: { ...(base.xAxis as object), data: stack.months.map(monthLabel) },
    tooltip: {
      ...(base.tooltip as object),
      formatter: (params: unknown) => {
        const i = (params as { dataIndex: number }[])[0]?.dataIndex ?? 0;
        const m = stack.months[i];
        if (m == null) return '';
        const rows = stack.series
          .map((s, k) => ({ s, k, v: s.values[i] ?? 0 }))
          .filter((r) => r.v !== 0)
          .reverse()
          .map((r) => tipRow(colors[r.k] ?? OTHER_COLOR, r.s.name, fmtStack(opts.unit, r.v)))
          .join('');
        return (
          tipHead(`${monthLabel(m)} · ${fmtStack(opts.unit, totals[i] ?? 0)}`) +
          (rows || tipRow(null, 'None', fmtStack(opts.unit, 0))) +
          (opts.note ? tipNote(opts.note) : '')
        );
      },
    },
    series: stack.series.map((s, k) => ({
      name: s.name,
      type: 'bar' as const,
      stack: 'total',
      barMaxWidth: 22,
      data: s.values,
      itemStyle: {
        color: colors[k] ?? OTHER_COLOR,
        borderColor: '#fff',
        borderWidth: 1,
        ...(k === lastIdx ? { borderRadius: BAR_RADIUS } : {}),
      },
    })),
  };
}

/** Quarterly lines, one per series; empty quarters leave a gap. Tooltip shows value and sample size. */
export function quarterLinesOption(lines: ExecQuarterLines, opts: { unit: LineUnit; note?: string }): EChartsOption {
  const base = baseOption({ yPercent: opts.unit === 'percent', yDays: opts.unit === 'days', left: 40 });
  const colors = seriesColors(lines.series.map((s) => s.name));
  return {
    ...base,
    xAxis: { ...(base.xAxis as object), data: lines.quarters, boundaryGap: true },
    tooltip: {
      ...(base.tooltip as object),
      axisPointer: { type: 'line', lineStyle: { color: INK.axis } },
      formatter: (params: unknown) => {
        const i = (params as { dataIndex: number }[])[0]?.dataIndex ?? 0;
        const q = lines.quarters[i];
        if (q == null) return '';
        const rows = lines.series
          .map((s, k) => {
            const n = s.n[i] ?? 0;
            return tipRow(colors[k] ?? OTHER_COLOR, s.name, `${fmtLine(opts.unit, s.values[i])} · n=${num(n)}`);
          })
          .join('');
        return tipHead(q) + rows + (opts.note ? tipNote(opts.note) : '');
      },
    },
    series: lines.series.map((s, k) => ({
      name: s.name,
      type: 'line' as const,
      connectNulls: false,
      symbol: 'circle',
      symbolSize: 6,
      data: s.values,
      lineStyle: { width: 2, color: colors[k] ?? OTHER_COLOR },
      itemStyle: { color: colors[k] ?? OTHER_COLOR },
    })),
  };
}

/** Latest non-null value of a series, with its quarter label. */
export function latestPoint(lines: ExecQuarterLines | undefined, seriesName: string): { quarter: string; value: number; n: number } | null {
  const s = lines?.series.find((x) => x.name === seriesName);
  if (!lines || !s) return null;
  for (let i = s.values.length - 1; i >= 0; i -= 1) {
    const v = s.values[i];
    if (v != null) return { quarter: lines.quarters[i] ?? '', value: v, n: s.n[i] ?? 0 };
  }
  return null;
}
